import { PhpArray, PhpFloat, fromJson, phpCopy, intval, floatval, strval, type PhpArrayInput, type PhpKeyInput, type PhpInt } from '../../php-compat';
import type { PortEnv } from '../../port-contract';

export type Arr = PhpArrayInput;
/** extract() leaves absent variables undefined until their first direct read. */
export function scopeValue(refs: Arr, key: string, env: Pick<PortEnv, 'warn'>): unknown {
    if (!(refs instanceof PhpArray ? refs.has(key) : Object.prototype.hasOwnProperty.call(refs, key))) env.warn('Undefined variable $' + key, 2);
    return slot(refs, key);
}
/** Typed PHP calls fail before entering a dependency or recording its effects. */
export function requireArrayArgument(value: unknown, fn: string, parameter: number, name: string): asserts value is Arr {
    if (!isArray(value)) throw new TypeError(`${fn}(): Argument #${parameter} ($${name}) must be of type array, ${phpType(value)} given`);
}
export function requireStringArgument(value: unknown, fn: string, parameter: number, name: string): string {
    if (value === null || value === undefined || isArray(value))
        throw new TypeError(`${fn}(): Argument #${parameter} ($${name}) must be of type string, ${phpType(value)} given`);
    return strval(value);
}
function phpType(value: unknown): string {
    if (value === null || value === undefined) return 'null';
    if (typeof value === 'boolean') return 'bool';
    if (typeof value === 'bigint' || typeof value === 'number' && Number.isSafeInteger(value)) return 'int';
    if (typeof value === 'number' || value instanceof PhpFloat) return 'float';
    return isArray(value) ? 'array' : typeof value;
}
/** Integer operators retain int64 precision and promote overflow to PHP double. */
export function integerProduct(a: PhpInt, b: PhpInt): PhpInt | PhpFloat {
    const product = BigInt(a) * BigInt(b);
    return product < -(1n << 63n) || product >= (1n << 63n) ? floatval(Number(a) * Number(b)) : intval(product);
}
/** Storage access only; game fields still use their existing injected readers. */
export function slot(value: unknown, ...path: PhpKeyInput[]): unknown {
    let current = value;
    for (const key of path) {
        if (current instanceof PhpArray) current = current.get(key);
        else if (current !== null && typeof current === 'object' && !(current instanceof PhpFloat))
            current = (current as Record<string, unknown>)[String(key)];
        else return null;
    }
    return current ?? null;
}
export function isArray(value: unknown): value is Arr {
    return value !== null && typeof value === 'object' && !(value instanceof PhpFloat);
}
export function array(value: Arr): PhpArray {
    return fromJson(phpCopy(value)) as PhpArray;
}
export function values(value: Arr): unknown[] {
    return value instanceof PhpArray ? [...value.values()] : Object.values(value);
}
/** Mutate owned/copied PHP storage; creates the same nested arrays as a PHP write. */
export function setPath(root: Arr, path: PhpKeyInput[], value: unknown): null {
    let current = root;
    for (const key of path.slice(0, -1)) {
        let child = slot(current, key);
        if (!isArray(child)) { child = new PhpArray(); put(current, key, child); }
        current = child as Arr;
    }
    put(current, path[path.length - 1]!, value);
    return null;
}
function put(root: Arr, key: PhpKeyInput, value: unknown): void {
    if (root instanceof PhpArray) root.set(key, value);
    else (root as Record<string, unknown>)[String(key)] = value;
}
export function unsetPath(root: Arr, path: PhpKeyInput[]): null {
    const parent = path.length === 1 ? root : slot(root, ...path.slice(0, -1));
    if (!isArray(parent)) return null;
    const key = path[path.length - 1]!;
    if (parent instanceof PhpArray) parent.delete(key);
    else delete (parent as Record<string, unknown>)[String(key)];
    return null;
}
export function appendPath(root: Arr, path: PhpKeyInput[], value: unknown): null {
    const existing = slot(root, ...path);
    const list = isArray(existing) ? array(existing) : new PhpArray();
    list.append(value); setPath(root, path, list);
    return null;
}
